import { useEffect, useRef, useState } from "react";
import { useCadastroFornecedor, useFornecedores } from "../../hooks/useFornecedores";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import Step1 from "./step1/Step1";
import Step2 from "./step2/Step2";
import Step3 from "./step3/Step3";
import Stepper from "../shared/stepper/Stepper";
import Validacoes from "../../utils/validacoes";
import Masks from "../../utils/masks";
import Alert from "../shared/alert/Alert";
import Style from "./index.module.css";

export const Cadastro = () => {
  const steps = ["Empresa", "Contatos", "Confirmação"];
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    cpfcnpj: "",
    razaoSocial: "",
    nomeFantasia: "",
    inscricaoEstadual: "",
    tipoServico: "",
    tipoPessoa: "JURIDICA",
    servicoSelecionado: "",
    uf: "",
    endereco: "",
    cidade: "",
    cep: "",
    emailPrincipal: "",
    emailSecundario: "",
    telefonePrincipal: "",
    telefoneSecundario: "",
    siteEmpresa: "",
    redeSocial: "",
    perfilRedeSocial: "",
  });

  const fieldLabels = {
    cpfcnpj: "CPF/CNPJ",
    razaoSocial: "Razão Social",
    nomeFantasia: "Nome Fantasia",
    inscricaoEstadual: "Inscrição Estadual",
    tipoServico: "Tipo de Serviço",
    servicoSelecionado: "Serviço Selecionado",
    contatoCliente: "Nome para Contato",
    uf: "UF",
    endereco: "Endereço",
    cidade: "Cidade",
    bairro: "Bairro",
    cep: "CEP",
    emailPrincipal: "E-mail Principal",
    emailSecundario: "E-mail Secundário",
    telefonePrincipal: "Telefone Principal",
    telefoneSecundario: "Telefone Secundário",
  };
  const { user } = useAuth();
  const navigate = useNavigate();
  const avisoPrimeiroAcesso = useRef(false);
  const dadosFornecedorCarregados = useRef(false);

  const isUserValid = user && user.codope;
  const isPrimeiroAcesso = isUserValid &&
    user.tipusu === "FORNECEDOR" &&
    (user.status === "PRIMEIRO_ACESSO" || user.status === "QUESTIONARIO_PENDENTE");

  const { fornecedor } = useFornecedores(
    user,
    user?.codope,
    isPrimeiroAcesso
  );

  useEffect(() => {
    if (!isPrimeiroAcesso || !fornecedor) return;
    if (dadosFornecedorCarregados.current) return;

    dadosFornecedorCarregados.current = true;

    setFormData(prev => ({
      ...prev,
      cpfcnpj: fornecedor.cgccli || "",
      razaoSocial: fornecedor.nomcli || "",
      nomeFantasia: fornecedor.fancli || "",
      inscricaoEstadual: fornecedor.insest || "",
      tipoServico: fornecedor.tipserv || "",
      servicoSelecionado: fornecedor.descserv || "",
      cep: fornecedor.cepcli || "",
      uf: fornecedor.estcid || "",
      bairro: fornecedor.baicli || "",
      endereco: fornecedor.endcli || "",
      cidade: fornecedor.nomcid || "",
      emailPrincipal: fornecedor.endele || "",
      emailSecundario: fornecedor.endalt || "",
      telefonePrincipal: fornecedor.foncla || "",
      telefoneSecundario: fornecedor.fonclb || "",
      siteEmpresa: fornecedor.siteurlcli || "",
      redeSocial: fornecedor.redesoccli || "",
      perfilRedeSocial: "",
      tipoPessoa: fornecedor.tipcli || "",
      contatoCliente: fornecedor.concli || "",
    }));

    if (!avisoPrimeiroAcesso.current) {
      avisoPrimeiroAcesso.current = true;

      if (user.status === "PRIMEIRO_ACESSO") {
        Alert({
          icon: "info",
          title: "Primeiro Acesso",
          message: "Identificamos que este é o seu primeiro acesso. Revise e complete seus dados antes de prosseguir.",
          showConfirmButton: true,
          confirmButtonText: "OK"
        });
      }
    }

  }, [user, fornecedor, isPrimeiroAcesso]);

  const {
    cadastrarFornecedorAsync,
    atualizarFornecedorAsync,
    isLoading
  } = useCadastroFornecedor();

  const handleChange = (name, value, tipoPessoa) => {
    if (name === "cpfcnpj") {
      value = tipoPessoa === "JURIDICA" ? Masks.cnpj(value) : Masks.cpf(value);
    } else if (name === "telefone") {
      value = Masks.telefone(value);
    } else if (name === "cep") {
      value = Masks.cep(value);
    } else if (name === "inscricaoEstadual") {
      value = tipoPessoa === "JURIDICA" ? Masks.inscricaoEstadual(value) : Masks.rg(value);
    } else if (name === "uf") {
      value = Masks.uf(value.toUpperCase());
    }

    setFormData(prev => ({ ...prev, [name]: value }));

    let errorMsg = "";

    switch (name) {
      case "cpfcnpj":
        if (tipoPessoa === "JURIDICA") {
          if (!Validacoes.cnpj(value)) errorMsg = "CNPJ inválido";
        } else if (tipoPessoa === "FISICA") {
          if (!Validacoes.cpf(value)) errorMsg = "CPF inválido";
        }
        break;
      case "inscricaoEstadual":
        if (tipoPessoa === "JURIDICA") {
          if (!Validacoes.inscricaoEstadual(value)) errorMsg = "Inscrição Estadual inválida";
        } else if (tipoPessoa === "FISICA") {
          if (!Validacoes.rg(value)) errorMsg = "RG inválido";
        }
        break;
      case "emailPrincipal":
      case "emailSecundario":
        if (!Validacoes.email(value)) errorMsg = "E-mail inválido";
        break;
      case "telefonePrincipal":
      case "telefoneSecundario":
        if (!Validacoes.telefone(value)) errorMsg = "Telefone inválido";
        break;
      case "siteEmpresa":
        if (value && !Validacoes.url(value)) errorMsg = "URL inválida";
        break;
      case "redeSocial":
        if (value && !["instagram", "facebook", "twitter"].includes(value)) {
          errorMsg = "Rede social inválida";
        }
        break;
      case "perfilRedeSocial":
        if (value && value.length < 3) {
          errorMsg = "Perfil muito curto";
        }
        break;
    }

    setErrors(prev => ({ ...prev, [name]: errorMsg }));
  };


  const nextStep = () => {
    let stepErrors = {};
    if (step === 1) {
      stepErrors = Validacoes.validarCamposObrigatorios(formData, [
        "cpfcnpj",
        "razaoSocial",
        "nomeFantasia",
        "inscricaoEstadual",
        "tipoServico",
        "servicoSelecionado",
        "uf",
        "endereco",
        "cidade",
        "bairro",
        "cep"
      ], fieldLabels);
    }
    if (step === 2) {
      stepErrors = Validacoes.validarCamposObrigatorios(formData, [
        "contatoCliente",
        "emailPrincipal",
        "emailSecundario",
        "telefonePrincipal",
        "telefoneSecundario"
      ], fieldLabels);
    }

    const hasValidationErrors = Object.values(errors).some(msg => msg && msg.length > 0);

    if (Object.keys(stepErrors).length > 0 || hasValidationErrors) {
      setErrors(prev => ({ ...prev, ...stepErrors }));
      return;
    }

    setStep(prev => Math.min(prev + 1, steps.length));
  };

  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));
  const goToStep = (n) => setStep(prev => (n < prev ? n : prev));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;

    const payload = {
      cgccli: formData.cpfcnpj,
      nomcli: formData.razaoSocial,
      fancli: formData.nomeFantasia,
      nomfancli: formData.nomeFantasia,
      insest: Masks.removeMask(formData.inscricaoEstadual),
      tipserv: formData.tipoServico,
      descserv: formData.servicoSelecionado,
      cepcli: formData.cep,
      estcid: formData.uf,
      baicli: formData.bairro || "",
      endcli: formData.endereco,
      codcid: formData.cidade,
      endelepri: formData.emailPrincipal,
      endelesec: formData.emailSecundario,
      contcli: formData.contatoCliente || "",
      foneclipri: Masks.removeMask(formData.telefonePrincipal),
      foneclisec: Masks.removeMask(formData.telefoneSecundario),
      celcli: Masks.removeMask(formData.telefonePrincipal) || "",
      siteurlcli: formData.siteEmpresa,
      redesoccli: formData.redeSocial,
      tipcli: formData.tipoPessoa === "JURIDICA" ? "2" : "1"
    };

    const confirmed = await Alert({
      icon: "warning",
      title: "Confirmar Cadastro",
      message: "Revise os dados inseridos. Essas informações serão enviadas para avaliação inicial do seu cadastro!",
      showConfirmButton: true,
      showCancelButton: true
    });

    if (confirmed) {
      try {
        let response = null;

        if (user?.tipusu === "FORNECEDOR" && (user?.status === "PRIMEIRO_ACESSO" || user?.status === "QUESTIONARIO_PENDENTE")) {
          response = await atualizarFornecedorAsync({ token: user?.token, codcli: user?.codope, payload });
          localStorage.setItem("user", JSON.stringify({ ...JSON.parse(localStorage.getItem("user") || "{}"), status: "QUESTIONARIO_PENDENTE" }));
        } else {
          response = await cadastrarFornecedorAsync(payload);
          navigate("/")
        }

        if (user?.status === "PRIMEIRO_ACESSO") {
          navigate("/home");
        }

        await Alert({
          icon: "success",
          title: "Sucesso",
          confirmButtonText: "OK",
          showConfirmButton: true,
          message: response.docs.mensagem,
          timer: 3500,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showCloseButton: false
        });

        if (user?.status === "QUESTIONARIO_PENDENTE") {
          navigate("/finalizacao_cadastro");
        } else {
          window.location.reload();
        }
      } catch (error) {
        const message = error.response?.data?.erro || error.message;

        await Alert({
          icon: "error",
          title: "Erro",
          message,
          showConfirmButton: true,
          confirmButtonText: "OK",
        });
      }
    }
  };

  return (
    <div className={Style.wrapper}>
      <section className={Style.stepperContainer}>
        <Stepper steps={steps} currentStep={step} onStepClick={goToStep} />
      </section>

      <header className={Style.header}>
        <h1>{user?.status === "PRIMEIRO_ACESSO" || user?.status === "QUESTIONARIO_PENDENTE" ? "Confirme seus dados" : "Cadastre-se"}</h1>
        <p>{user?.status === "PRIMEIRO_ACESSO" || user?.status === "QUESTIONARIO_PENDENTE" ? "Confirme os dados de cadastro" : "Preencha os dados de cadastro"}. Isso levará apenas alguns minutos.</p>
      </header>

      <section className={Style.formWrapper}>
        <form className={Style.formContainer} onSubmit={handleSubmit}>
          {step === 1 && (
            <Step1
              formData={formData}
              handleChange={handleChange}
              errors={errors}
              nextStep={nextStep}
              isLoading={isLoading}
            />
          )}
          {step === 2 && (
            <Step2
              formData={formData}
              handleChange={(e) => handleChange(e.target.name, e.target.value)}
              errors={errors}
              prevStep={prevStep}
              nextStep={nextStep}
              isLoading={isLoading}
            />
          )}
          {step === 3 && (
            <Step3
              formData={formData}
              prevStep={prevStep}
              errors={errors}
              handleSubmit={handleSubmit}
              isLoading={isLoading}
            />
          )}
        </form>
      </section>
    </div>
  );
};


